import { toast } from "react-toastify";
import { useForm, Controller } from "react-hook-form";
import useQuestsAPI from "../../../hooks/useQuestsAPI";
import { Link, useNavigate, useParams } from "react-router-dom";
import useQuestRegisterAPI from "../../../hooks/useQuestRegisterAPI.js";
import { Button } from "react-bootstrap";
import {
  TextField,
  Checkbox,
  FormControlLabel,
  Typography,
  FormGroup,
  RadioGroup,
  Radio,
  FormHelperText,
} from "@mui/material";
import {
  TextFieldCustomization,
  FillCustomization,
  LabelCustomization,
  RadioGroupCustomization,
} from "./materialUI customization.js";
import { useAuth0 } from "@auth0/auth0-react";
import { useEffect, useState } from "react";
import { useRamadan } from "../../../context/RamadanContext";
import { getApiErrorMessage } from "../../../utils/getApiErrorMessage";

/**
 * QuestRegistration Component
 * Component to show inputs required for registration for a form while also handling the required API requests.
 *
 * @returns {JSX.Element} The rendered component.
 */
const QuestRegistration = () => {
  const { isRamadan } = useRamadan();

  const navigate = useNavigate();
  const { user } = useAuth0();
  const isClient = user.role === "client";
  // Initialize react-hook-form
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm();

  // Get quest data
  const { id } = useParams();
  const { isLoading, data, error } = useQuestsAPI(id);
  // Handle if enter invalid id in url
  const res = error ? error.response : null;
  useEffect(() => {
    if (res && res.status === 404) {
      navigate("/page-error-404");
    }
  }, [res, navigate]);

  // Flag for end of phase
  let phaseEnded =
    data?.phase !== "registration" || data?.registrationDeadline < new Date();

  if (data.type === "race" && data.phase === "submission") {
    phaseEnded = false;
  }

  // Quest registration custom hook
  const {
    questRegistrationRequest,
    questQuestions,
    isLoading: isRegistrationLoading,
  } = useQuestRegisterAPI(id);

  // Register user for quest functionality
  const onSubmit = async (formData) => {
    try {
      // Validate readQuest field - must be "yes"
      if (formData.readQuest !== "yes") {
        toast.error(
          "You must confirm that you have read and understood the quest requirements"
        );
        return;
      }
      // Transform commitment and qualification values to readable format
      const commitmentMap = {
        "very-confident": "very confident",
        "confident": "confident",
        "somewhat-confident": "somewhat confident",
        "prefer-not-to-say": "prefer Not To Say",
      };

      const qualificationMap = {
        "perfect-match": "perfect match",
        "great-match": "great match",
        "good-match": "good match",
        "prefer-not-to-say": "prefer Not To Say",
      };

      const transformedFormData = { ...formData };
      if (transformedFormData.commitment) {
        transformedFormData.commitment = commitmentMap[transformedFormData.commitment] || transformedFormData.commitment;
      }
      if (transformedFormData.qualification) {
        transformedFormData.qualification = qualificationMap[transformedFormData.qualification] || transformedFormData.qualification;
      }

      // Prepare the registration data with boolean fields
      const registrationData = {
        ...transformedFormData,
        ...data,
      };


      // Send registration request
      await questRegistrationRequest(registrationData);

      // Notify user and navigate
      toast.success("Registered");
      setTimeout(() => {
        navigate("/quest-list");
      }, 3500);
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

    return (
    <>
      <div className="d-flex align-items-center mb-4 flex-wrap justify-content-end">
        <Button
          className="me-2"
          variant="outline-primary btn-rounded"
          onClick={() => navigate(`/quest-view/${id}`)}
        >
          <i className="fa-solid fa-chevron-left"></i>{" "}
          <span style={{ margin: "0 10px" }}>Back</span>
        </Button>
      </div>

      <div className="quest-view">
        <div className="card w-100">
          {/* WAIT FOR RESPONSE */}
          {isLoading ? (
            <div className="card-header d-flex align-items-center justify-content-start gap-5 flex-wrap">
              <h3 className="mb-4 me-auto">Loading...</h3>
            </div>
          ) : (
            <>
              <div className="card-header d-flex align-items-center justify-content-start gap-5 flex-wrap">
                <h3>{`${id} - ${data.title} Quest`}</h3>
                {phaseEnded && (
                  <h3 className="color-primary">Registration Phase Ended!</h3>
                )}
              </div>
              <form onSubmit={handleSubmit(onSubmit)}>
                {/* QUEST QUESTIONS */}
                {questQuestions && questQuestions.length > 0 && (
                  <div className="card-body pb-0">
                    <h3>Quest Related Questions</h3>
                    <div className="places mb-5">
                      <div className="d-flex gap-4 flex-column mb-1 text-inputs-width">
                        {questQuestions.map((question) => (
                          <Controller
                            key={question.id}
                            name={question.name}
                            control={control}
                            defaultValue={
                              question.type == "boolean" ? "true" : ""
                            }
                            rules={{ required: question.required }}
                            render={({ field }) =>
                              question.type == "boolean" ? (
                                <div>
                                  <p className="mb-0 fs-5">{question.label}</p>
                                  <RadioGroup
                                    {...field}
                                    sx={RadioGroupCustomization}
                                  >
                                    <FormControlLabel
                                      value="true"
                                      control={<Radio sx={FillCustomization} />}
                                      label="Yes"
                                    />
                                    <FormControlLabel
                                      value="false"
                                      control={<Radio sx={FillCustomization} />}
                                      label="No"
                                    />
                                  </RadioGroup>
                                </div>
                              ) : (
                                <TextField
                                  {...field}
                                  label={question.label}
                                  variant="standard"
                                  sx={TextFieldCustomization}
                                  fullWidth
                                  error={!!errors[question.name]}
                                  helperText={
                                    errors[question.name]
                                      ? "This field is required"
                                      : ""
                                  }
                                />
                              )
                            }
                          />
                        ))}
                      </div>
                    </div>
                    <hr />
                  </div>
                )}

                {/* TERMS & CONDITIONS */}
                <div className="card-body">
                  <h3>Terms and Conditions</h3>
                  <p className="user-agreement" style={{ fontSize: "1rem" }}>
                    By Registering to a quest, you agree to the{" "}
                    <a
                      href="https://code-quests.com/privacy-policy-2/"
                      className="link"
                    >
                      terms of service of code-quests.com
                    </a>{" "}
                    If you have any questions please reach to{" "}
                    <a href="mailto:support@code-quests.com" className="link">
                      support@code-quests.com
                    </a>
                  </p>

                  {data.isHiring ? (
                    <FormGroup>
                      <Controller
                        name="terms1"
                        control={control}
                        defaultValue={false}
                        rules={{ required: true }}
                        render={({ field }) => (
                          <>
                            <FormControlLabel
                              control={
                                <Checkbox {...field} sx={FillCustomization} />
                              }
                              sx={LabelCustomization}
                              label={<span style={{ fontSize: "1rem" }}>Details regarding salary, benefits, and other compensation will be discussed directly with the hiring company if you are selected for an interview or job offer. Code Quests does not determine or guarantee compensation for hiring quests.</span>}
                            />
                            {errors.terms1 && (
                              <FormHelperText error>
                                This field is required.
                              </FormHelperText>
                            )}
                          </>
                        )}
                      />

                      <Controller
                        name="terms2"
                        control={control}
                        defaultValue={false}
                        rules={{ required: true }}
                        render={({ field }) => (
                          <>
                            <FormControlLabel
                              control={
                                <Checkbox {...field} sx={FillCustomization} />
                              }
                              sx={LabelCustomization}
                              label={<span style={{ fontSize: "1rem" }}>By proceeding, you agree that your profile information may be shared with the hiring company for this quest. This helps them assess your suitability for potential opportunities and streamlines the hiring process.</span>}
                            />
                            {errors.terms2 && (
                              <FormHelperText error>
                                This field is required.
                              </FormHelperText>
                            )}
                          </>
                        )}
                      />
                    </FormGroup>
                  ) : (
                    <>
                      <FormGroup>
                        <Controller
                          name="terms1"
                          control={control}
                          defaultValue={false}
                          rules={{ required: true }}
                          render={({ field }) => (
                            <>
                              <FormControlLabel
                                control={
                                  <Checkbox {...field} sx={FillCustomization} />
                                }
                                sx={LabelCustomization}
                                label={<span style={{ fontSize: "1rem" }}>Quests payment is applicable for Egyptian nationals only.</span>}
                              />
                              {errors.terms1 && (
                                <FormHelperText error>
                                  This field is required.
                                </FormHelperText>
                              )}
                            </>
                          )}
                        />
                        <Controller
                          name="terms2"
                          control={control}
                          defaultValue={false}
                          rules={{ required: true }}
                          render={({ field }) => (
                            <>
                              <FormControlLabel
                                control={
                                  <Checkbox {...field} sx={FillCustomization} />
                                }
                                sx={LabelCustomization}
                                label={
                                  <span style={{ fontSize: "1rem" }}>
                                    I understand and agree that if my submitted
                                    work (code, designs, etc.) wins a prize in
                                    this quest,{" "}
                                    <span
                                      style={{
                                        color: "#F93A0B",
                                        fontWeight: "600",
                                      }}
                                    >
                                      its copyrights and ownership will be
                                      transferred to Code Quests and its
                                      clients.
                                    </span>
                                  </span>
                                }
                              />
                              {errors.terms2 && (
                                <FormHelperText error>
                                  This field is required.
                                </FormHelperText>
                              )}
                            </>
                          )}
                        />
                        <Controller
                          name="terms3"
                          control={control}
                          defaultValue={false}
                          rules={{ required: true }}
                          render={({ field }) => (
                            <>
                              <FormControlLabel
                                control={
                                  <Checkbox {...field} sx={FillCustomization} />
                                }
                                sx={LabelCustomization}
                                label={<span style={{ fontSize: "1rem" }}>You will not share personal or private information about other contestants like names, emails, phone numbers, or employment status.</span>}
                              />
                              {errors.terms3 && (
                                <FormHelperText error>
                                  This field is required.
                                </FormHelperText>
                              )}
                            </>
                          )}
                        />
                        <Controller
                          name="terms4"
                          control={control}
                          defaultValue={false}
                          rules={{ required: true }}
                          render={({ field }) => (
                            <>
                              <FormControlLabel
                                control={
                                  <Checkbox {...field} sx={FillCustomization} />
                                }
                                sx={LabelCustomization}
                                label={<span style={{ fontSize: "1rem" }}>You will not share information about the projects, requirements, market analysis, source code, or any other documents shared with you by the project manager or others in the quest.</span>}
                              />
                              {errors.terms4 && (
                                <FormHelperText error>
                                  This field is required.
                                </FormHelperText>
                              )}
                            </>
                          )}
                        />
                      </FormGroup>
                    </>
                  )}
                </div>

                {/* FIRST CONTEST */}
                <div className="card-body pt-0">
                  <h4>
                    Is This Your First {data.isHiring ? "Hiring" : "Task-Based"}{" "}
                    Quest With Us?
                  </h4>

                  <Controller
                    name="firstTime"
                    control={control}
                    rules={{ required: true }}
                    defaultValue="yes"
                    render={({ field }) => (
                      <RadioGroup {...field} sx={RadioGroupCustomization}>
                        <FormControlLabel
                          value="yes"
                          control={<Radio sx={FillCustomization} />}
                          label="Yes"
                        />
                        <FormControlLabel
                          value="no"
                          control={<Radio sx={FillCustomization} />}
                          label="No"
                        />
                      </RadioGroup>
                    )}
                  />
                </div>

                {/* READ QUEST */}
                <div className="card-body pt-0">
                  <h4>
                    I Have Read The Quest Carefully And Understand What's
                    Expected - I'am Ready To Start The Task
                  </h4>

                  <Controller
                    name="readQuest"
                    control={control}
                    rules={{ required: true }}
                    defaultValue="no"
                    render={({ field }) => (
                      <RadioGroup {...field} sx={RadioGroupCustomization}>
                        <FormControlLabel
                          value="yes"
                          control={<Radio sx={FillCustomization} />}
                          label="Yes"
                        />
                        <FormControlLabel
                          value="no"
                          control={<Radio sx={FillCustomization} />}
                          label="No"
                        />
                      </RadioGroup>
                    )}
                  />
                </div>

                {data.isHiring ? (
                  <div className="card-body pt-0 text-black">
                    {/* Quest Commitment Section */}
                    <div
                      className="border rounded mb-3"
                      style={{ borderColor: "#dee2e6", overflow: "hidden" }}
                    >
                      <div
                        className="border-bottom"
                        style={{
                          borderColor: "#dee2e6",
                          padding: "1rem",
                          background: "#f3f3f3",
                          fontSize: "1rem",
                          fontWeight: "600",
                        }}
                      >
                        <div className="d-flex align-items-center">
                          <i
                            className="bi bi-clock"
                            style={{
                              width: "24px",
                              height: "24px",
                              fontSize: "1.2rem",
                              color: "rgb(11 11 138)",
                            }}
                          ></i>
                          <span className="">Your Quest Commitment</span>
                        </div>
                      </div>

                      <div className="p-3">
                        <p
                          className=""
                          style={{ fontSize: "0.975rem", margin: "0" }}
                        >
                          How confident are you that you will successfully
                          complete and submit this quest?
                        </p>

                        <Controller
                          name="commitment"
                          control={control}
                          rules={{ required: true }}
                          defaultValue=""
                          render={({ field }) => (
                            <RadioGroup
                              {...field}
                              sx={RadioGroupCustomization}
                              style={{ flexDirection: "column", gap: 0 }}
                            >
                              <FormControlLabel
                                value="very-confident"
                                control={<Radio sx={FillCustomization} />}
                                label={
                                  <span style={{ fontSize: "0.975rem" }}>
                                    <span
                                      className=""
                                      style={{ fontWeight: "600" }}
                                    >
                                      Very Confident
                                    </span>
                                    <span
                                      className="text-muted"
                                      style={{ color: "#888888" }}
                                    >
                                      {" "}
                                      - I'm Fully Committed And Have The Time!
                                    </span>
                                  </span>
                                }
                                sx={LabelCustomization}
                              />
                              <FormControlLabel
                                value="confident"
                                control={<Radio sx={FillCustomization} />}
                                label={
                                  <span style={{ fontSize: "0.975rem" }}>
                                    <span
                                      className=""
                                      style={{ fontWeight: "600" }}
                                    >
                                      Confident
                                    </span>
                                    <span
                                      className="text-muted"
                                      style={{ color: "#888888" }}
                                    >
                                      {" "}
                                      - I Have A Clear Plan And Schedule
                                    </span>
                                  </span>
                                }
                                sx={LabelCustomization}
                              />
                              <FormControlLabel
                                value="somewhat-confident"
                                control={<Radio sx={FillCustomization} />}
                                label={
                                  <span style={{ fontSize: "0.975rem" }}>
                                    <span
                                      className=""
                                      style={{ fontWeight: "600" }}
                                    >
                                      Somewhat Confident
                                    </span>
                                    <span
                                      className="text-muted"
                                      style={{ color: "#888888" }}
                                    >
                                      {" "}
                                      - I'll Give It My Best Effort
                                    </span>
                                  </span>
                                }
                                sx={LabelCustomization}
                              />
                              <FormControlLabel
                                value="prefer-not-to-say"
                                control={<Radio sx={FillCustomization} />}
                                label={
                                  <span style={{ fontSize: "0.975rem" }}>
                                    <span
                                      className="fw-medium"
                                      style={{ color: "#888888" }}
                                    >
                                      Prefer Not To Say
                                    </span>
                                  </span>
                                }
                                sx={LabelCustomization}
                              />
                            </RadioGroup>
                          )}
                        />
                        {errors.commitment && (
                          <FormHelperText error>
                            This field is required.
                          </FormHelperText>
                        )}
                      </div>
                    </div>

                    {/* Quest-Qualification Match Section */}
                    <div
                      className="border rounded mb-3"
                      style={{ borderColor: "#dee2e6", overflow: "hidden" }}
                    >
                      <div
                        className="border-bottom"
                        style={{
                          borderColor: "#dee2e6",
                          padding: "1rem",
                          background: "#f3f3f3",
                          fontSize: "1rem",
                          fontWeight: "600",
                        }}
                      >
                        <div className="d-flex align-items-center">
                          <i
                            className="bi bi-award"
                            style={{
                              width: "24px",
                              height: "24px",
                              fontSize: "1.2rem",
                              color: "#28a745",
                            }}
                          ></i>
                          <span className="">Quest-Qualification Match</span>
                        </div>
                      </div>

                      <div className="p-3">
                        <p
                          className=""
                          style={{ fontSize: "0.975rem", margin: "0" }}
                        >
                          How well does this quest align with your current
                          skills and qualifications?
                        </p>

                        <Controller
                          name="qualification"
                          control={control}
                          rules={{ required: true }}
                          defaultValue=""
                          render={({ field }) => (
                            <RadioGroup
                              {...field}
                              sx={RadioGroupCustomization}
                              style={{ flexDirection: "column", gap: 0 }}
                            >
                              <FormControlLabel
                                value="perfect-match"
                                control={<Radio sx={FillCustomization} />}
                                label={
                                  <span style={{ fontSize: "0.975rem" }}>
                                    <span
                                      className=""
                                      style={{
                                        color: "rgb(33 131 55)",
                                        fontWeight: "600",
                                      }}
                                    >
                                      Perfect Match
                                    </span>
                                    <span
                                      className="text-muted"
                                      style={{ color: "#888888" }}
                                    >
                                      {" "}
                                      - Exactly What I'm Looking For!
                                    </span>
                                  </span>
                                }
                                sx={LabelCustomization}
                              />
                              <FormControlLabel
                                value="great-match"
                                control={<Radio sx={FillCustomization} />}
                                label={
                                  <span style={{ fontSize: "0.975rem" }}>
                                    <span
                                      className=""
                                      style={{
                                        color: "rgb(32 57 220)",
                                        fontWeight: "600",
                                      }}
                                    >
                                      Great Match
                                    </span>
                                    <span
                                      className="text-muted"
                                      style={{ color: "#888888" }}
                                    >
                                      {" "}
                                      - Strong Alignment With My Skills
                                    </span>
                                  </span>
                                }
                                sx={LabelCustomization}
                              />
                              <FormControlLabel
                                value="good-match"
                                control={<Radio sx={FillCustomization} />}
                                label={
                                  <span style={{ fontSize: "0.975rem" }}>
                                    <span
                                      className=""
                                      style={{
                                        color: "rgb(199 49 12)",
                                        fontWeight: "600",
                                      }}
                                    >
                                      Good Match
                                    </span>
                                    <span
                                      className="text-muted"
                                      style={{ color: "#888888" }}
                                    >
                                      {" "}
                                      - I Can Learn And Grow With This Quest
                                    </span>
                                  </span>
                                }
                                sx={LabelCustomization}
                              />
                              <FormControlLabel
                                value="prefer-not-to-say"
                                control={<Radio sx={FillCustomization} />}
                                label={
                                  <span style={{ fontSize: "0.975rem" }}>
                                    <span
                                      className=""
                                      style={{ color: "#888888" }}
                                    >
                                      Prefer Not To Say
                                    </span>
                                  </span>
                                }
                                sx={LabelCustomization}
                              />
                            </RadioGroup>
                          )}
                        />
                        {errors.qualification && (
                          <FormHelperText error>
                            This field is required.
                          </FormHelperText>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* FIRST CONTEST */}
                    <FormGroup>
                      {/* Meeting Information Section */}
                      <div
                        className="card-body pt-0"
                        style={{ paddingBottom: "1rem" }}
                      >
                        <h4>Meeting Information</h4>
                        <p className="text-muted" style={{ marginBottom: "0" }}>
                          Live Online Meetings Maybe Be Held To Discuss Quest
                          Details, Provide Guidance, And Answer Questions. Your
                          Active Participation Is Highly Encouraged And May Be
                          Mandatory For Certain Quest Stages.
                        </p>

                        <Controller
                          name="meetingInfo"
                          control={control}
                          defaultValue={false}
                          rules={{ required: true }}
                          render={({ field }) => (
                            <>
                              <FormControlLabel
                                control={
                                  <Checkbox {...field} sx={FillCustomization} />
                                }
                                sx={LabelCustomization}
                                label={
                                  <span className="text-black">
                                    I understand that attending quest meetings
                                    is an important part of this experience.
                                  </span>
                                }
                              />
                              {errors.meetingInfo && (
                                <FormHelperText error>
                                  This field is required.
                                </FormHelperText>
                              )}
                            </>
                          )}
                        />
                      </div>

                      {/* Deadlines And Submission Section */}
                      <div
                        className="card-body pt-0"
                        style={{ paddingBottom: "1rem" }}
                      >
                        <h4>Deadlines And Submission</h4>
                        <p className="text-muted" style={{ marginBottom: "0" }}>
                          All Quest Submissions Must Adhere Strictly To The
                          Published Deadlines. Late Submissions Will Not Be
                          Accepted And May Result In Disqualification.
                        </p>

                        <Controller
                          name="deadlinesSubmission"
                          control={control}
                          defaultValue={false}
                          rules={{ required: true }}
                          render={({ field }) => (
                            <>
                              <FormControlLabel
                                control={
                                  <Checkbox {...field} sx={FillCustomization} />
                                }
                                sx={LabelCustomization}
                                label={
                                  <span className="text-black">
                                    I acknowledge and agree to submit my work
                                    before the specified deadlines
                                  </span>
                                }
                              />
                              {errors.deadlinesSubmission && (
                                <FormHelperText error>
                                  This field is required.
                                </FormHelperText>
                              )}
                            </>
                          )}
                        />
                      </div>
                    </FormGroup>
                  </>
                )}

                {/* SUBMIT */}
                <div className="card-footer border-0">
                  {!phaseEnded && (
                    <button
                      className={`btn btn-primary px-5 mb-4 fs-14 ${
                        isRamadan && "ramadan-button"
                      }`}
                      type="submit"
                      disabled={isRegistrationLoading || isClient}
                    >
                      {isRamadan && <div></div>}
                      <span className="fs-14" style={{ fontWeight: "bold" }}>
                        REGISTER
                      </span>
                    </button>
                  )}
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default QuestRegistration;
